import { useEffect } from "react";
import { Route, Switch } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "react-query";
import CmsLayout from "./CmsLayout";
import CmsMainLayout from "./CmsMainLayout";
import Dashboard from "./pages/shared/dashboard/Dashboard";
import Language from "./pages/Language";
import Languages from "./pages/Languages";
import Countries from "./pages/Countries";
import Country from "./pages/Country";
import Talents from "./pages/Talents";
import TalentCategoryDetails from "./pages/TalentCategoryDetails";
import TalentDocuments from "./pages/TalentDocuments";
import Talent from "./pages/Talent";
import Users from "./pages/Users";
import User from "./pages/User";
import SignIn from "./pages/SignIn";
import PublicRoute from "./utils/PublicRoute";
import PrivateRoute from "./utils/PrivateRoute";
import SignOut from "./pages/SignOut";
import Courses from "./pages/Courses";
import Course from "./pages/Course";
import CourseDetails from "./pages/CourseDetails";
import Categories from "./pages/posts/Categories";
import UserTalent from "./pages/UserTalent";
import Referee from "./pages/referee";
import Juries from "./pages/Juries";
import TalentQuestions from "./pages/TalentQuestions";
import Ratings from "./pages/Ratings";
import Cities from "./pages/Cities";
import Administrators from "./pages/Administrators";
import Posts from "./pages/posts/Posts";
import Post from "./pages/posts/Post";
import Audience from "./pages/Audience";
import Settings from "./pages/Settings";
import SettingsGeneral from "./pages/SettingsGeneral";
import SettingsSlideshow from "./pages/SettingsSlideshow";
import About from "./pages/About";
import CourseDocuments from "./pages/CourseDocuments";
import Banned from "./pages/Banned";
import Testimonials from "./pages/Testimonials";
import Testimonial from "./pages/Testimonial";
import Notifications from "./pages/Notifications";
import Administrator from "./pages/Administrator";
import Editors from "./pages/Editors";
import Editor from "./pages/Editor";
import Messages from "./pages/Messages";
import Message from "./pages/Message";
import Translate from "./pages/Translate";
import Emails from "./pages/Emails";
import Email from "./pages/Email";
import Faqs from "./pages/Faqs";
import Faq from "./pages/Faq";
import Pages from "./pages/Pages";
import Page from "./pages/Page";
import VideoGalleryList from "./pages/VideoGalleryList";
import VideoGallery from "./pages/VideoGallery";
import Online from "./pages/Online";
import ContestStatistics from "./pages/ContestStatistics";
import PopupMessageList from "./pages/PopupMessageList";
import PopupMessage from "./pages/PopupMessage";
import CourseHistory from "./pages/contest/CourseHistory";
import CourseHistoryDetails from "./pages/contest/CourseHistoryDetails";
import Polls from "./pages/contest/Polls";
import Poll from "./pages/contest/Poll";
import PollReport from "./pages/contest/PollReport";
import RatingsSummary from "./pages/contest/RatingsSummary";
import RatingsSelector from "./pages/contest/RatingsSelector";
import CourseDocumentsSelector from "./pages/CourseDocumentsSelector";
import VideoGalleries from "cms/pages/gallery/video_gallery/video_galleries";
import { ApiBase } from "./utils/Network";
import { isCmsLoginPath, redirectToCmsLogin } from "./utils";

const cmsQueryClient = new QueryClient();
const CMS_API_PATH_PATTERN = /\/(api|auth|postapi|base|rep)\//;
const CMS_LOGIN_API_PATH = /\/auth\/login\/?$/;

function getRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.url === "string") return input.url;
  return "";
}

function isCmsApiRequest(url) {
  if (!url || typeof window === "undefined") return false;
  try {
    const parsed = new URL(url, window.location.origin);
    if (!CMS_API_PATH_PATTERN.test(parsed.pathname)) return false;
    const apiOrigin = new URL(ApiBase, window.location.origin).origin;
    return parsed.origin === apiOrigin || parsed.origin === window.location.origin;
  } catch (error) {
    return false;
  }
}

function isCmsLoginApiRequest(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return CMS_LOGIN_API_PATH.test(parsed.pathname);
  } catch (error) {
    return false;
  }
}

function CmsRoutes() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);

      const isCmsPath = window.location.pathname.startsWith("/cms");
      const requestUrl = getRequestUrl(input);

      if (
        isCmsPath &&
        !isCmsLoginPath(window.location.pathname) &&
        response.status === 401 &&
        isCmsApiRequest(requestUrl) &&
        !isCmsLoginApiRequest(requestUrl)
      ) {
        redirectToCmsLogin();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return (
    <QueryClientProvider client={cmsQueryClient}>
      <CmsLayout>
      <Switch>
        <PublicRoute component={SignIn} exact path="/cms/login" />
        <PublicRoute component={SignIn} exact path="/cms/signin" />
        <Route component={SignOut} exact path="/cms/signout" />
        <Route
          path="/cms"
          render={() => (
            <CmsMainLayout>
              <Switch>
        <PrivateRoute component={Dashboard} exact path="/cms/dashboard" />
        <PrivateRoute component={Dashboard} exact path="/cms" />
        <PrivateRoute component={Online} exact path="/cms/online" />
        <PrivateRoute component={ContestStatistics} exact path="/cms/conteststats" />
        <PrivateRoute path="/cms/language/" exact component={Language} />
        <PrivateRoute path="/cms/languages/:id" component={Languages} />
        <PrivateRoute path="/cms/languages" component={Languages} exact />
        <PrivateRoute path="/cms/countries" component={Countries} exact />
        <PrivateRoute path="/cms/countries/:page" component={Countries} exact />
        <PrivateRoute path="/cms/country" component={Country} exact />
        <PrivateRoute path="/cms/country/:id" component={Country} exact />
        <PrivateRoute path="/cms/cities" component={Cities} exact />

        <PrivateRoute path="/cms/talents" component={Talents} exact />
        <PrivateRoute path="/cms/talents/new" component={TalentCategoryDetails} exact />
        <PrivateRoute path="/cms/talents/:id" component={TalentCategoryDetails} exact />
        <PrivateRoute path="/cms/talents/:id/documents" exact component={TalentDocuments} />
        <PrivateRoute path="/cms/talents/:id/documents/:document_id" component={TalentDocuments} />
        <PrivateRoute path="/cms/talents/:id/questions/" exact component={TalentQuestions} />
        <PrivateRoute path="/cms/talents/:id/questions/:question_id" component={TalentQuestions} />
        <PrivateRoute path="/cms/talent/:id" exact component={Talent} />
        <PrivateRoute path="/cms/talent/:id/:page" component={Talent} />
        <PrivateRoute path="/cms/usertalent/:id" component={UserTalent} />
        <PrivateRoute path="/cms/videogalleries" component={VideoGalleries} />
        <PrivateRoute path="/cms/users" component={Users} exact />
        <PrivateRoute path="/cms/users/:page" component={Users} exact />
        <PrivateRoute path="/cms/banned" component={Banned} exact />
        <PrivateRoute path="/cms/banned/:page" component={Banned} exact />
        <PrivateRoute path="/cms/user" exact component={User} />
        <PrivateRoute path="/cms/administrator" exact component={Administrator} />
        <PrivateRoute path="/cms/administrator/:id" exact component={Administrator} />
        <PrivateRoute path="/cms/editor" exact component={Editor} />
        <PrivateRoute path="/cms/editor/:id" exact component={Editor} />
        <PrivateRoute path="/cms/user/:id" component={User} />
        <PrivateRoute path="/cms/jury" exact component={Referee} />
        <PrivateRoute path="/cms/jury/:id" component={Referee} />
        <PrivateRoute path="/cms/juries" component={Juries} />
        <PrivateRoute path="/cms/audience" component={Audience} exact />
        <PrivateRoute path="/cms/audience/:page" component={Audience} />
        <PrivateRoute path="/cms/administrators" exact component={Administrators} />
        <PrivateRoute path="/cms/administrators/:page" component={Administrators} />

        <PrivateRoute path="/cms/editors" exact component={Editors} />
        <PrivateRoute path="/cms/editors/:page" component={Editors} />

        <PrivateRoute path="/cms/courses" component={Courses} exact />
        <PrivateRoute path="/cms/courses/:page" component={Courses} exact />
        <PrivateRoute path="/cms/course" exact component={Course} />
        <PrivateRoute path="/cms/course/:id" component={Course} />
        <PrivateRoute path="/cms/coursedetails/:id" exact component={CourseDetails} />
        <PrivateRoute path="/cms/coursedetails/:id/:page" component={CourseDetails} />

        <PrivateRoute path="/cms/ratings/old" component={Ratings} exact />
        <PrivateRoute path="/cms/ratings" component={RatingsSelector} exact />
        <PrivateRoute path="/cms/ratings/:courseId" component={RatingsSummary} exact />
        <PrivateRoute path="/cms/coursedocuments" component={CourseDocumentsSelector} exact />
        <PrivateRoute path="/cms/coursedocuments/:courseId" component={CourseDocuments} exact />
        <PrivateRoute path="/cms/about" component={About} exact />
        <PrivateRoute path="/cms/settings/general" component={SettingsGeneral} exact />
        <PrivateRoute path="/cms/settings/slideshow" component={SettingsSlideshow} exact />
        <PrivateRoute path="/cms/settings" component={Settings} exact />

        <PrivateRoute path="/cms/categories" component={Categories} exact />
        <PrivateRoute path="/cms/categories/:id" component={Categories} exact />
        <PrivateRoute path="/cms/posts" component={Posts} exact />
        <PrivateRoute path="/cms/posts/:page" component={Posts} exact />
        <PrivateRoute path="/cms/post" component={Post} exact />
        <PrivateRoute path="/cms/post/:id" component={Post} exact />

        <PrivateRoute path="/cms/testimonials" component={Testimonials} exact />
        <PrivateRoute path="/cms/testimonials/:page" component={Testimonials} exact />
        <PrivateRoute path="/cms/messages" component={Messages} exact />
        <PrivateRoute path="/cms/messages/:page" component={Messages} exact />
        <PrivateRoute path="/cms/message/:id" component={Message} exact />

        <PrivateRoute path="/cms/testimonial" component={Testimonial} exact />
        <PrivateRoute path="/cms/testimonial/:id" component={Testimonial} exact />

        <PrivateRoute path="/cms/notifications" component={Notifications} exact />

        <PrivateRoute path="/cms/translate" component={Translate} exact />
        <PrivateRoute path="/cms/translate/:id" component={Translate} exact />
        <PrivateRoute path="/cms/translations" component={Translate} exact />
        <PrivateRoute path="/cms/translations/:id" component={Translate} exact />

        <PrivateRoute path="/cms/emails" component={Emails} exact />
        <PrivateRoute path="/cms/email" component={Email} exact />
        <PrivateRoute path="/cms/email/:id" component={Email} exact />
        <PrivateRoute path="/cms/emails/:page" component={Emails} exact />
        <PrivateRoute path="/cms/faqs/:page" component={Faqs} exact />
        <PrivateRoute path="/cms/faqs" component={Faqs} exact />
        <PrivateRoute path="/cms/faq/" component={Faq} exact />
        <PrivateRoute path="/cms/faq/:id" component={Faq} exact />

        <PrivateRoute path="/cms/pages" component={Pages} exact />
        <PrivateRoute path="/cms/pages/:page" component={Pages} exact />
        <PrivateRoute path="/cms/page" component={Page} exact />
        <PrivateRoute path="/cms/page/:id" component={Page} />

        <PrivateRoute path="/cms/videogallerylist/" component={VideoGalleryList} exact />
        <PrivateRoute path="/cms/videogallery" component={VideoGallery} exact />
        <PrivateRoute path="/cms/videogallery/:id" component={VideoGallery} />

        <PrivateRoute path="/cms/popupmessages/" component={PopupMessageList} exact />
        <PrivateRoute path="/cms/popupmessage" component={PopupMessage} exact />
        <PrivateRoute path="/cms/popupmessage/:id" component={PopupMessage} />

        <PrivateRoute path="/cms/coursehistories" component={CourseHistory} exact />
        <PrivateRoute path="/cms/coursehistory" component={CourseHistoryDetails} exact />
        <PrivateRoute path="/cms/coursehistory/:id" component={CourseHistoryDetails} />

        <PrivateRoute path="/cms/polls" component={Polls} exact />
        <PrivateRoute path="/cms/poll" component={Poll} exact />
        <PrivateRoute path="/cms/poll/report/:id" component={PollReport} />
        <PrivateRoute path="/cms/poll/:id" component={Poll} />
              </Switch>
            </CmsMainLayout>
          )}
        />
      </Switch>
      </CmsLayout>
    </QueryClientProvider>
  );
}

export default CmsRoutes;
